import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import {
    Droplets,
    Flower,
    Flower2,
    Leaf,
    Sprout
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface ProductEmblemProps {
    categorySlug: string | null
    size?: 'card' | 'large'
    className?: string
}

const CATEGORY_ICONS: Record<string, LucideIcon> = {
    'saffron': Flower,
    'mountain-herbs': Sprout,
    'herbal-teas': Flower2,
    'essential-oils': Droplets
}

export const categoryIcon = (slug: string | null): LucideIcon => {
    return (slug && CATEGORY_ICONS[slug]) || Leaf
}

/* Apothecary label artwork, shown while a product has no photo in the CMS */
export const ProductEmblem: FC<ProductEmblemProps> = ({
    categorySlug,
    size = 'card',
    className
}): ReactNode => {
    const Icon = categoryIcon(categorySlug)
    const large = size === 'large'

    return (
        <div
            aria-hidden='true'
            className={cn('relative flex size-full flex-col items-center justify-center overflow-hidden bg-forest-900 text-gold', className)}
        >
            <div className='absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(201,162,74,0.24),transparent_60%)]' />
            <div className='bg-girih absolute inset-0 opacity-[0.07]' />

            {large ? (
                <div className='absolute inset-5 rounded-t-full rounded-b-[1.5rem] border border-gold/25' />
            ) : (
                <>
                    <div className='absolute inset-3 rounded-[1.25rem] border border-gold/25' />
                    <span className='absolute start-5 top-5 size-1.5 rotate-45 bg-gold/70' />
                    <span className='absolute end-5 top-5 size-1.5 rotate-45 bg-gold/70' />
                </>
            )}

            <span className={cn('absolute size-1.5 rotate-45 bg-gold/70', large ? 'bottom-9 start-9' : 'bottom-5 start-5')} />
            <span className={cn('absolute size-1.5 rotate-45 bg-gold/70', large ? 'bottom-9 end-9' : 'bottom-5 end-5')} />

            <span className={cn('relative font-medium uppercase tracking-[0.35em] text-gold/70', large ? 'text-xs' : 'text-[0.6rem]')}>
                KhuzKimia Zagros
            </span>

            <div
                className={cn(
                    'relative mt-4 flex items-center justify-center rounded-full border border-gold/60 bg-forest-950/40 shadow-[0_0_60px_-10px_rgba(201,162,74,0.45)]',
                    large ? 'size-40' : 'size-24'
                )}
            >
                <div className='absolute inset-1.5 rounded-full border border-gold/25' />
                <Icon className={large ? 'size-16' : 'size-10'} strokeWidth={1.2} />
            </div>

            <span className='relative mt-4 flex items-center gap-2'>
                <span className='h-px w-8 bg-gold/50' />
                <span className='size-1 rotate-45 bg-gold' />
                <span className='h-px w-8 bg-gold/50' />
            </span>
        </div>
    )
}
